// @ts-nocheck
import type { ValidationIssue } from '../../diagnostics';
import type { ExpressionDefinition } from '../../types/script';
import { resolveRef } from '../binding/BindingResolver';
import type { BindingContext } from '../binding/types';
import type {
    ExpressionAstNode,
    ExpressionBinaryNode,
    ExpressionCallPureNode,
    ExpressionEvaluationOptions,
    ExpressionEvaluationResult,
    ExpressionEngine,
    ExpressionIfNode,
    ExpressionPrimitive,
    ExpressionUnaryNode,
    PureFunctionName,
    TimeBetweenUnit,
} from './types';
import {
  adjustColorBrightness,
  adjustColorSaturation,
  applyColorWithAlpha,
  mergeColorComponents,
  shiftColorHue,
  splitColorChannel,
  type ColorSpace,
  type ColorSplitChannel,
} from './colorExpressionUtils';
import {
  hostCreateDateTimeFromParts,
  hostGetDateTimePart,
  type DateTimePart,
  type DateTimeTimezone,
} from '../host/HostDateTimeHelpers';
import { convertQuantityValue } from '../units/convertQuantity';
import { listUnitsJson } from '../units/listUnits';
import { isUnitDimension } from '../units/types';

const COLOR_PURE_FUNCTIONS: readonly PureFunctionName[] = [
  'colorWithAlpha',
  'colorBrighter',
  'colorDarker',
  'colorSaturate',
  'colorDesaturate',
  'colorShiftHue',
];

function isPureFunctionName(value: string): value is PureFunctionName {
  return (
    value === 'daysBetween'
    || value === 'timeBetween'
    || value === 'parseNumber'
    || value === 'convertQuantity'
    || value === 'listUnits'
    || value === 'colorSplit'
    || value === 'colorMerge'
    || value === 'dateTimeSplit'
    || value === 'dateTimeMerge'
    || COLOR_PURE_FUNCTIONS.includes(value as PureFunctionName)
  );
}

function isValidPureFunctionArgCount(fn: PureFunctionName, count: number): boolean {
  if (fn === 'timeBetween') {
    return count === 3;
  }
  if (fn === 'parseNumber') {
    return count === 1;
  }
  if (fn === 'convertQuantity') {
    return count >= 3 && count <= 5;
  }
  if (fn === 'listUnits') {
    return count >= 0 && count <= 2;
  }
  if (fn === 'colorSplit') {
    return count === 3;
  }
  if (fn === 'colorMerge') {
    return count === 4;
  }
  if (fn === 'dateTimeSplit') {
    return count === 3;
  }
  if (fn === 'dateTimeMerge') {
    return count === 7;
  }
  if (COLOR_PURE_FUNCTIONS.includes(fn)) {
    return count === 2;
  }
  return count === 2;
}

function pureFunctionArgCount(fn: PureFunctionName): number {
  if (fn === 'timeBetween') {
    return 3;
  }
  if (fn === 'parseNumber') {
    return 1;
  }
  if (fn === 'convertQuantity') {
    return 3;
  }
  if (fn === 'listUnits') {
    return 0;
  }
  if (fn === 'colorSplit') {
    return 3;
  }
  if (fn === 'colorMerge') {
    return 4;
  }
  if (fn === 'dateTimeSplit') {
    return 3;
  }
  if (fn === 'dateTimeMerge') {
    return 7;
  }
  return 2;
}

const MS_PER_DAY = 86_400_000;
const MS_PER_WEEK = 7 * MS_PER_DAY;
const MS_PER_MONTH = (365.25 / 12) * MS_PER_DAY;

const DEFAULT_PROFILE = 'production' as const;

function issue(code: ValidationIssue['code'], message: string, path?: string): ValidationIssue {
  return { severity: 'error', code, message, path };
}

function asNode(root: Record<string, unknown>): ExpressionAstNode | null {
  if (!root || typeof root !== 'object') {
    return null;
  }
  const kind = (root as { kind?: unknown }).kind;
  if (typeof kind !== 'string') {
    return null;
  }

  if (kind === 'literal') {
    const value = (root as { value?: unknown }).value;
    if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return { kind: 'literal', value };
    }
    return null;
  }

  if (kind === 'ref') {
    const ref = (root as { ref?: unknown }).ref;
    if (ref && typeof ref === 'object' && typeof (ref as { kind?: unknown }).kind === 'string') {
      return { kind: 'ref', ref: ref as ExpressionAstNode extends { kind: 'ref'; ref: infer R } ? R : never };
    }
    return null;
  }

  if (kind === 'unary') {
    const op = (root as { op?: unknown }).op;
    const arg = (root as { arg?: unknown }).arg;
    if ((op === 'not' || op === 'neg') && arg && typeof arg === 'object') {
      const parsedArg = asNode(arg as Record<string, unknown>);
      if (!parsedArg) {
        return null;
      }
      return { kind: 'unary', op, arg: parsedArg };
    }
    return null;
  }

  if (kind === 'binary') {
    const op = (root as { op?: unknown }).op;
    const left = (root as { left?: unknown }).left;
    const right = (root as { right?: unknown }).right;
    if (typeof op !== 'string' || !left || !right || typeof left !== 'object' || typeof right !== 'object') {
      return null;
    }
    const parsedLeft = asNode(left as Record<string, unknown>);
    const parsedRight = asNode(right as Record<string, unknown>);
    if (!parsedLeft || !parsedRight) {
      return null;
    }
    return { kind: 'binary', op: op as ExpressionBinaryNode['op'], left: parsedLeft, right: parsedRight };
  }

  if (kind === 'if') {
    const when = (root as { when?: unknown }).when;
    const then = (root as { then?: unknown }).then;
    const elseValue = (root as { else?: unknown }).else;
    if (!when || !then || !elseValue || typeof when !== 'object' || typeof then !== 'object' || typeof elseValue !== 'object') {
      return null;
    }
    const parsedWhen = asNode(when as Record<string, unknown>);
    const parsedThen = asNode(then as Record<string, unknown>);
    const parsedElse = asNode(elseValue as Record<string, unknown>);
    if (!parsedWhen || !parsedThen || !parsedElse) {
      return null;
    }
    return {
      kind: 'if',
      when: parsedWhen,
      then: parsedThen,
      else: parsedElse,
    } as ExpressionIfNode;
  }

  if (kind === 'callPure') {
    const fn = (root as { function?: unknown }).function;
    const args = (root as { args?: unknown }).args;
    if (typeof fn !== 'string' || !isPureFunctionName(fn)) {
      return null;
    }
    if (!Array.isArray(args)) {
      return null;
    }
    if (!isValidPureFunctionArgCount(fn, args.length)) {
      return null;
    }
    if (fn === 'timeBetween') {
      const unitArg = args[2];
      if (!unitArg || typeof unitArg !== 'object') {
        return null;
      }
      const unitNode = asNode(unitArg as Record<string, unknown>);
      if (!unitNode || unitNode.kind !== 'literal' || typeof unitNode.value !== 'string') {
        return null;
      }
      if (!isTimeBetweenUnit(unitNode.value)) {
        return null;
      }
    }
    if (fn === 'colorSplit') {
      const spaceArg = args[1];
      const channelArg = args[2];
      if (!spaceArg || typeof spaceArg !== 'object' || !channelArg || typeof channelArg !== 'object') {
        return null;
      }
      const spaceNode = asNode(spaceArg as Record<string, unknown>);
      const channelNode = asNode(channelArg as Record<string, unknown>);
      if (
        !spaceNode
        || spaceNode.kind !== 'literal'
        || typeof spaceNode.value !== 'string'
        || !isColorSpace(spaceNode.value)
      ) {
        return null;
      }
      if (
        !channelNode
        || channelNode.kind !== 'literal'
        || typeof channelNode.value !== 'string'
        || !isColorSplitChannelForSpace(spaceNode.value, channelNode.value)
      ) {
        return null;
      }
    }
    if (fn === 'colorMerge') {
      const spaceArg = args[3];
      if (!spaceArg || typeof spaceArg !== 'object') {
        return null;
      }
      const spaceNode = asNode(spaceArg as Record<string, unknown>);
      if (
        !spaceNode
        || spaceNode.kind !== 'literal'
        || typeof spaceNode.value !== 'string'
        || !isColorSpace(spaceNode.value)
      ) {
        return null;
      }
    }
    if (fn === 'dateTimeSplit') {
      const timezoneArg = args[1];
      const partArg = args[2];
      if (!timezoneArg || typeof timezoneArg !== 'object' || !partArg || typeof partArg !== 'object') {
        return null;
      }
      const timezoneNode = asNode(timezoneArg as Record<string, unknown>);
      const partNode = asNode(partArg as Record<string, unknown>);
      if (
        !timezoneNode
        || timezoneNode.kind !== 'literal'
        || typeof timezoneNode.value !== 'string'
        || !isDateTimeTimezone(timezoneNode.value)
      ) {
        return null;
      }
      if (
        !partNode
        || partNode.kind !== 'literal'
        || typeof partNode.value !== 'string'
        || !isDateTimePartName(partNode.value)
      ) {
        return null;
      }
    }
    if (fn === 'dateTimeMerge') {
      const timezoneArg = args[6];
      if (!timezoneArg || typeof timezoneArg !== 'object') {
        return null;
      }
      const timezoneNode = asNode(timezoneArg as Record<string, unknown>);
      if (
        !timezoneNode
        || timezoneNode.kind !== 'literal'
        || typeof timezoneNode.value !== 'string'
        || !isDateTimeTimezone(timezoneNode.value)
      ) {
        return null;
      }
    }
    const parsedArgs: ExpressionAstNode[] = [];
    for (const rawArg of args) {
      if (!rawArg || typeof rawArg !== 'object') {
        return null;
      }
      const parsedArg = asNode(rawArg as Record<string, unknown>);
      if (!parsedArg) {
        return null;
      }
      parsedArgs.push(parsedArg);
    }
    return { kind: 'callPure', function: fn as PureFunctionName, args: parsedArgs };
  }

  return null;
}

const TIME_BETWEEN_UNITS: readonly TimeBetweenUnit[] = ['days', 'weeks', 'months'];

const RGB_CHANNELS: readonly ColorSplitChannel[] = ['r', 'g', 'b'];
const HSL_CHANNELS: readonly ColorSplitChannel[] = ['h', 's', 'l'];

function isColorSpace(value: string): value is ColorSpace {
  return value === 'rgb' || value === 'hsl';
}

function isColorSplitChannelForSpace(colorSpace: ColorSpace, channel: string): channel is ColorSplitChannel {
  const allowed = colorSpace === 'rgb' ? RGB_CHANNELS : HSL_CHANNELS;
  return (allowed as readonly string[]).includes(channel);
}

function parseColorSpaceLiteral(node: ExpressionAstNode): ColorSpace | null {
  if (node.kind !== 'literal' || typeof node.value !== 'string') {
    return null;
  }
  return isColorSpace(node.value) ? node.value : null;
}

function parseColorSplitChannelLiteral(
  node: ExpressionAstNode,
  colorSpace: ColorSpace,
): ColorSplitChannel | null {
  if (node.kind !== 'literal' || typeof node.value !== 'string') {
    return null;
  }
  return isColorSplitChannelForSpace(colorSpace, node.value) ? node.value : null;
}

function isTimeBetweenUnit(value: string): value is TimeBetweenUnit {
  return (TIME_BETWEEN_UNITS as readonly string[]).includes(value);
}

function parseTimeBetweenUnit(node: ExpressionAstNode): TimeBetweenUnit | null {
  if (node.kind !== 'literal' || typeof node.value !== 'string') {
    return null;
  }
  return isTimeBetweenUnit(node.value) ? node.value : null;
}

const DATE_TIME_PARTS: readonly DateTimePart[] = [
  'year',
  'month',
  'day',
  'hour',
  'minute',
  'second',
];

function isDateTimeTimezone(value: string): value is DateTimeTimezone {
  return value === 'local' || value === 'utc';
}

function isDateTimePartName(value: string): value is DateTimePart {
  return (DATE_TIME_PARTS as readonly string[]).includes(value);
}

function parseDateTimeTimezoneLiteral(node: ExpressionAstNode): DateTimeTimezone | null {
  if (node.kind !== 'literal' || typeof node.value !== 'string') {
    return null;
  }
  return isDateTimeTimezone(node.value) ? node.value : null;
}

function parseDateTimePartLiteral(node: ExpressionAstNode): DateTimePart | null {
  if (node.kind !== 'literal' || typeof node.value !== 'string') {
    return null;
  }
  return isDateTimePartName(node.value) ? node.value : null;
}

function msDiffToUnit(msDiff: number, unit: TimeBetweenUnit): number {
  switch (unit) {
    case 'days':
      return Math.floor(msDiff / MS_PER_DAY);
    case 'weeks':
      return Math.floor(msDiff / MS_PER_WEEK);
    case 'months':
      return Math.floor(msDiff / MS_PER_MONTH);
  }
}

function evalTimeBetweenArgs(
  args: ExpressionAstNode[],
  ctx: BindingContext,
  options: Required<ExpressionEvaluationOptions>,
  unit: TimeBetweenUnit,
): ExpressionEvaluationResult {
  const left = evalNode(args[0], ctx, options);
  if (!left.ok) {
    return left;
  }
  const right = evalNode(args[1], ctx, options);
  if (!right.ok) {
    return right;
  }
  const msA = parseDateMs(left.value);
  const msB = parseDateMs(right.value);
  if (msA === null || msB === null) {
    return {
      ok: false,
      value: null,
      issues: [
        issue(
          'expression.type_mismatch',
          'timeBetween arguments must be valid dates or timestamps.',
        ),
      ],
    };
  }
  return { ok: true, value: msDiffToUnit(msB - msA, unit), issues: [] };
}

function parseDateMs(value: ExpressionPrimitive): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return null;
}

function evalStringLiteralOrValue(
  node: ExpressionAstNode,
  ctx: BindingContext,
  options: Required<ExpressionEvaluationOptions>,
): string | null {
  const result = evalNode(node, ctx, options);
  if (!result.ok) {
    return null;
  }
  if (typeof result.value === 'string') {
    return result.value;
  }
  if (typeof result.value === 'number' || typeof result.value === 'boolean') {
    return String(result.value);
  }
  return null;
}

function evalOptionalStringLiteralOrValue(
  node: ExpressionAstNode,
  ctx: BindingContext,
  options: Required<ExpressionEvaluationOptions>,
): string | undefined {
  const value = evalStringLiteralOrValue(node, ctx, options);
  if (value === null || value.trim() === '') {
    return undefined;
  }
  return value;
}

function evalCallPure(
  node: ExpressionCallPureNode,
  ctx: BindingContext,
  options: Required<ExpressionEvaluationOptions>,
): ExpressionEvaluationResult {
  if (node.function === 'daysBetween') {
    if (node.args.length !== 2) {
      return {
        ok: false,
        value: null,
        issues: [issue('expression.type_mismatch', 'daysBetween requires exactly two arguments.')],
      };
    }
    return evalTimeBetweenArgs(node.args, ctx, options, 'days');
  }

  if (node.function === 'timeBetween') {
    if (node.args.length !== 3) {
      return {
        ok: false,
        value: null,
        issues: [issue('expression.type_mismatch', 'timeBetween requires exactly three arguments.')],
      };
    }
    const unit = parseTimeBetweenUnit(node.args[2]);
    if (unit === null) {
      return {
        ok: false,
        value: null,
        issues: [
          issue(
            'expression.type_mismatch',
            'timeBetween third argument must be a literal unit: days, weeks, or months.',
          ),
        ],
      };
    }
    return evalTimeBetweenArgs(node.args, ctx, options, unit);
  }

  if (node.function === 'parseNumber') {
    if (node.args.length !== 1) {
      return {
        ok: false,
        value: null,
        issues: [issue('expression.type_mismatch', 'parseNumber requires exactly one argument.')],
      };
    }
    const arg = evalNode(node.args[0], ctx, options);
    if (!arg.ok) {
      return arg;
    }
    const n = toNumber(arg.value);
    if (n === null) {
      return {
        ok: false,
        value: null,
        issues: [issue('expression.type_mismatch', 'parseNumber argument must be numeric or numeric string.')],
      };
    }
    return { ok: true, value: n, issues: [] };
  }

  if (node.function === 'convertQuantity') {
    if (node.args.length < 3 || node.args.length > 5) {
      return {
        ok: false,
        value: null,
        issues: [issue('expression.type_mismatch', 'convertQuantity requires 3 to 5 arguments.')],
      };
    }
    const valueArg = evalNode(node.args[0], ctx, options);
    if (!valueArg.ok) {
      return valueArg;
    }
    const value = toNumber(valueArg.value);
    if (value === null) {
      return {
        ok: false,
        value: null,
        issues: [issue('expression.type_mismatch', 'convertQuantity value must be numeric.')],
      };
    }
    const fromUnit = evalStringLiteralOrValue(node.args[1], ctx, options);
    if (fromUnit === null) {
      return {
        ok: false,
        value: null,
        issues: [issue('expression.type_mismatch', 'convertQuantity fromUnit must be a string.')],
      };
    }
    const toUnit = evalStringLiteralOrValue(node.args[2], ctx, options);
    if (toUnit === null) {
      return {
        ok: false,
        value: null,
        issues: [issue('expression.type_mismatch', 'convertQuantity toUnit must be a string.')],
      };
    }
    const material = node.args[3]
      ? evalOptionalStringLiteralOrValue(node.args[3], ctx, options)
      : undefined;
    const locale = node.args[4]
      ? evalOptionalStringLiteralOrValue(node.args[4], ctx, options)
      : undefined;
    return {
      ok: true,
      value: convertQuantityValue({ value, fromUnit, toUnit, material, locale }),
      issues: [],
    };
  }

  if (node.function === 'listUnits') {
    if (node.args.length > 2) {
      return {
        ok: false,
        value: null,
        issues: [issue('expression.type_mismatch', 'listUnits accepts 0 to 2 arguments.')],
      };
    }
    let dimension: import('../units/types').UnitDimension | undefined;
    if (node.args[0]) {
      const dimensionRaw = evalStringLiteralOrValue(node.args[0], ctx, options);
      if (dimensionRaw === null) {
        return {
          ok: false,
          value: null,
          issues: [issue('expression.type_mismatch', 'listUnits dimension must be a string literal.')],
        };
      }
      if (!isUnitDimension(dimensionRaw)) {
        return {
          ok: false,
          value: null,
          issues: [
            issue(
              'expression.type_mismatch',
              'listUnits dimension must be length, area, volume, weight, time, or pressure.',
            ),
          ],
        };
      }
      dimension = dimensionRaw;
    }
    const locale = node.args[1]
      ? evalOptionalStringLiteralOrValue(node.args[1], ctx, options)
      : undefined;
    return { ok: true, value: listUnitsJson({ dimension, locale }), issues: [] };
  }

  if (
    node.function === 'colorWithAlpha'
    || node.function === 'colorBrighter'
    || node.function === 'colorDarker'
    || node.function === 'colorSaturate'
    || node.function === 'colorDesaturate'
    || node.function === 'colorShiftHue'
  ) {
    return evalColorPureCall(node, ctx, options);
  }

  if (node.function === 'colorSplit' || node.function === 'colorMerge') {
    return evalColorSplitMergeCall(node, ctx, options);
  }

  if (node.function === 'dateTimeSplit' || node.function === 'dateTimeMerge') {
    return evalDateTimeSplitMergeCall(node, ctx, options);
  }

  return {
    ok: false,
    value: null,
    issues: [issue('expression.invalid_operator', `Unsupported pure function: ${node.function}`)],
  };
}

function evalColorPureCall(
  node: ExpressionCallPureNode,
  ctx: BindingContext,
  options: Required<ExpressionEvaluationOptions>,
): ExpressionEvaluationResult {
  if (node.args.length !== 2) {
    return {
      ok: false,
      value: null,
      issues: [issue('expression.type_mismatch', `${node.function} requires exactly two arguments.`)],
    };
  }

  const colorArg = evalNode(node.args[0], ctx, options);
  if (!colorArg.ok) {
    return colorArg;
  }
  if (typeof colorArg.value !== 'string') {
    return {
      ok: false,
      value: null,
      issues: [issue('expression.type_mismatch', `${node.function} color argument must be a string.`)],
    };
  }

  const amountArg = evalNode(node.args[1], ctx, options);
  if (!amountArg.ok) {
    return amountArg;
  }
  const amount = toNumber(amountArg.value);
  if (amount === null) {
    return {
      ok: false,
      value: null,
      issues: [issue('expression.type_mismatch', `${node.function} amount argument must be numeric.`)],
    };
  }

  let result: string | null = null;
  switch (node.function) {
    case 'colorWithAlpha':
      result = applyColorWithAlpha(colorArg.value, amount);
      break;
    case 'colorBrighter':
      result = adjustColorBrightness(colorArg.value, Math.abs(amount));
      break;
    case 'colorDarker':
      result = adjustColorBrightness(colorArg.value, -Math.abs(amount));
      break;
    case 'colorSaturate':
      result = adjustColorSaturation(colorArg.value, Math.abs(amount));
      break;
    case 'colorDesaturate':
      result = adjustColorSaturation(colorArg.value, -Math.abs(amount));
      break;
    case 'colorShiftHue':
      result = shiftColorHue(colorArg.value, amount);
      break;
    default:
      break;
  }

  if (result === null) {
    return {
      ok: false,
      value: null,
      issues: [issue('expression.type_mismatch', `${node.function} received an invalid color value.`)],
    };
  }

  return { ok: true, value: result, issues: [] };
}

function evalColorSplitMergeCall(
  node: ExpressionCallPureNode,
  ctx: BindingContext,
  options: Required<ExpressionEvaluationOptions>,
): ExpressionEvaluationResult {
  if (node.function === 'colorSplit') {
    if (node.args.length !== 3) {
      return {
        ok: false,
        value: null,
        issues: [issue('expression.type_mismatch', 'colorSplit requires exactly three arguments.')],
      };
    }

    const colorArg = evalNode(node.args[0], ctx, options);
    if (!colorArg.ok) {
      return colorArg;
    }
    if (typeof colorArg.value !== 'string') {
      return {
        ok: false,
        value: null,
        issues: [issue('expression.type_mismatch', 'colorSplit color argument must be a string.')],
      };
    }

    const colorSpace = parseColorSpaceLiteral(node.args[1]);
    if (!colorSpace) {
      return {
        ok: false,
        value: null,
        issues: [
          issue(
            'expression.type_mismatch',
            'colorSplit second argument must be a literal color space: rgb or hsl.',
          ),
        ],
      };
    }

    const channel = parseColorSplitChannelLiteral(node.args[2], colorSpace);
    if (!channel) {
      return {
        ok: false,
        value: null,
        issues: [
          issue(
            'expression.type_mismatch',
            `colorSplit third argument must be a literal channel valid for ${colorSpace}.`,
          ),
        ],
      };
    }

    const result = splitColorChannel(colorArg.value, colorSpace, channel);
    if (result === null) {
      return {
        ok: false,
        value: null,
        issues: [issue('expression.type_mismatch', 'colorSplit received an invalid color value.')],
      };
    }

    return { ok: true, value: result, issues: [] };
  }

  if (node.args.length !== 4) {
    return {
      ok: false,
      value: null,
      issues: [issue('expression.type_mismatch', 'colorMerge requires exactly four arguments.')],
    };
  }

  const aArg = evalNode(node.args[0], ctx, options);
  if (!aArg.ok) {
    return aArg;
  }
  const bArg = evalNode(node.args[1], ctx, options);
  if (!bArg.ok) {
    return bArg;
  }
  const cArg = evalNode(node.args[2], ctx, options);
  if (!cArg.ok) {
    return cArg;
  }

  const a = toNumber(aArg.value);
  const b = toNumber(bArg.value);
  const c = toNumber(cArg.value);
  if (a === null || b === null || c === null) {
    return {
      ok: false,
      value: null,
      issues: [issue('expression.type_mismatch', 'colorMerge component arguments must be numeric.')],
    };
  }

  const colorSpace = parseColorSpaceLiteral(node.args[3]);
  if (!colorSpace) {
    return {
      ok: false,
      value: null,
      issues: [
        issue(
          'expression.type_mismatch',
          'colorMerge fourth argument must be a literal color space: rgb or hsl.',
        ),
      ],
    };
  }

  const result = mergeColorComponents(a, b, c, colorSpace);
  if (result === null) {
    return {
      ok: false,
      value: null,
      issues: [issue('expression.type_mismatch', 'colorMerge received invalid component values.')],
    };
  }

  return { ok: true, value: result, issues: [] };
}

function evalDateTimeSplitMergeCall(
  node: ExpressionCallPureNode,
  ctx: BindingContext,
  options: Required<ExpressionEvaluationOptions>,
): ExpressionEvaluationResult {
  if (node.function === 'dateTimeSplit') {
    if (node.args.length !== 3) {
      return {
        ok: false,
        value: null,
        issues: [issue('expression.type_mismatch', 'dateTimeSplit requires exactly three arguments.')],
      };
    }

    const isoArg = evalNode(node.args[0], ctx, options);
    if (!isoArg.ok) {
      return isoArg;
    }
    if (typeof isoArg.value !== 'string' && typeof isoArg.value !== 'number') {
      return {
        ok: false,
        value: null,
        issues: [issue('expression.type_mismatch', 'dateTimeSplit datetime argument must be a string or number.')],
      };
    }

    const timezone = parseDateTimeTimezoneLiteral(node.args[1]);
    if (!timezone) {
      return {
        ok: false,
        value: null,
        issues: [
          issue(
            'expression.type_mismatch',
            'dateTimeSplit second argument must be a literal timezone: local or utc.',
          ),
        ],
      };
    }

    const part = parseDateTimePartLiteral(node.args[2]);
    if (!part) {
      return {
        ok: false,
        value: null,
        issues: [
          issue(
            'expression.type_mismatch',
            'dateTimeSplit third argument must be a literal part: year, month, day, hour, minute, or second.',
          ),
        ],
      };
    }

    const hostResult = hostGetDateTimePart({ iso: isoArg.value, part, timezone });
    if (!hostResult.ok) {
      return {
        ok: false,
        value: null,
        issues: [
          issue(
            'expression.type_mismatch',
            hostResult.error.message || 'dateTimeSplit received an invalid datetime value.',
          ),
        ],
      };
    }

    return { ok: true, value: hostResult.value, issues: [] };
  }

  if (node.args.length !== 7) {
    return {
      ok: false,
      value: null,
      issues: [issue('expression.type_mismatch', 'dateTimeMerge requires exactly seven arguments.')],
    };
  }

  const partValues: number[] = [];
  for (let i = 0; i < 6; i += 1) {
    const arg = evalNode(node.args[i], ctx, options);
    if (!arg.ok) {
      return arg;
    }
    const n = toNumber(arg.value);
    if (n === null) {
      return {
        ok: false,
        value: null,
        issues: [issue('expression.type_mismatch', 'dateTimeMerge part arguments must be numeric.')],
      };
    }
    partValues.push(n);
  }

  const timezone = parseDateTimeTimezoneLiteral(node.args[6]);
  if (!timezone) {
    return {
      ok: false,
      value: null,
      issues: [
        issue(
          'expression.type_mismatch',
          'dateTimeMerge seventh argument must be a literal timezone: local or utc.',
        ),
      ],
    };
  }

  const hostResult = hostCreateDateTimeFromParts({
    year: partValues[0],
    month: partValues[1],
    day: partValues[2],
    hour: partValues[3],
    minute: partValues[4],
    second: partValues[5],
    timezone,
  });
  if (!hostResult.ok) {
    return {
      ok: false,
      value: null,
      issues: [
        issue(
          'expression.type_mismatch',
          hostResult.error.message || 'dateTimeMerge received invalid datetime parts.',
        ),
      ],
    };
  }

  return { ok: true, value: hostResult.value, issues: [] };
}

function toBoolean(value: ExpressionPrimitive): boolean | null {
  if (typeof value === 'boolean') {
    return value;
  }
  return null;
}

function toNumber(value: ExpressionPrimitive): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

function evalUnary(node: ExpressionUnaryNode, ctx: BindingContext, options: Required<ExpressionEvaluationOptions>): ExpressionEvaluationResult {
  const arg = evalNode(node.arg, ctx, options);
  if (!arg.ok) {
    return arg;
  }

  if (node.op === 'not') {
    const boolValue = toBoolean(arg.value);
    if (boolValue === null) {
      return { ok: false, value: null, issues: [issue('expression.type_mismatch', 'Operator not requires a boolean operand.')] };
    }
    return { ok: true, value: !boolValue, issues: [] };
  }

  if (node.op === 'neg') {
    const numericValue = toNumber(arg.value);
    if (numericValue === null) {
      return { ok: false, value: null, issues: [issue('expression.type_mismatch', 'Operator neg requires a numeric operand.')] };
    }
    return { ok: true, value: -numericValue, issues: [] };
  }

  return { ok: false, value: null, issues: [issue('expression.invalid_operator', 'Unsupported unary operator: ' + node.op)] };
}

function evalBinary(node: ExpressionBinaryNode, ctx: BindingContext, options: Required<ExpressionEvaluationOptions>): ExpressionEvaluationResult {
  const left = evalNode(node.left, ctx, options);
  if (!left.ok) {
    return left;
  }
  const right = evalNode(node.right, ctx, options);
  if (!right.ok) {
    return right;
  }

  switch (node.op) {
    case 'add':
    case 'sub':
    case 'mul':
    case 'div': {
      const leftNum = toNumber(left.value);
      const rightNum = toNumber(right.value);
      if (leftNum === null || rightNum === null) {
        return { ok: false, value: null, issues: [issue('expression.type_mismatch', `Operator ${node.op} requires numeric operands.`)] };
      }
      if (node.op === 'add') {
        return { ok: true, value: leftNum + rightNum, issues: [] };
      }
      if (node.op === 'sub') {
        return { ok: true, value: leftNum - rightNum, issues: [] };
      }
      if (node.op === 'mul') {
        return { ok: true, value: leftNum * rightNum, issues: [] };
      }
      if (rightNum === 0) {
        return { ok: false, value: null, issues: [issue('expression.type_mismatch', 'Division by zero is not allowed.')] };
      }
      return { ok: true, value: leftNum / rightNum, issues: [] };
    }

    case 'and':
    case 'or':
    case 'xor':
    case 'nand':
    case 'nor': {
      const leftBool = toBoolean(left.value);
      const rightBool = toBoolean(right.value);
      if (leftBool === null || rightBool === null) {
        return { ok: false, value: null, issues: [issue('expression.type_mismatch', `Operator ${node.op} requires boolean operands.`)] };
      }
      if (node.op === 'and') {
        return { ok: true, value: leftBool && rightBool, issues: [] };
      }
      if (node.op === 'or') {
        return { ok: true, value: leftBool || rightBool, issues: [] };
      }
      if (node.op === 'xor') {
        return { ok: true, value: leftBool !== rightBool, issues: [] };
      }
      if (node.op === 'nand') {
        return { ok: true, value: !(leftBool && rightBool), issues: [] };
      }
      return { ok: true, value: !(leftBool || rightBool), issues: [] };
    }

    case 'eq':
      return { ok: true, value: left.value === right.value, issues: [] };
    case 'neq':
      return { ok: true, value: left.value !== right.value, issues: [] };

    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const leftNum = toNumber(left.value);
      const rightNum = toNumber(right.value);
      if (leftNum === null || rightNum === null) {
        return { ok: false, value: null, issues: [issue('expression.type_mismatch', `Operator ${node.op} requires numeric operands.`)] };
      }
      if (node.op === 'gt') {
        return { ok: true, value: leftNum > rightNum, issues: [] };
      }
      if (node.op === 'gte') {
        return { ok: true, value: leftNum >= rightNum, issues: [] };
      }
      if (node.op === 'lt') {
        return { ok: true, value: leftNum < rightNum, issues: [] };
      }
      return { ok: true, value: leftNum <= rightNum, issues: [] };
    }

    default:
      return {
        ok: false,
        value: null,
        issues: [issue('expression.invalid_operator', 'Unsupported binary operator: ' + node.op)],
      };
  }
}

function evalExpressionLibraryRef(
  expressionId: string,
  ctx: BindingContext,
  options: Required<ExpressionEvaluationOptions>,
): ExpressionEvaluationResult {
  if (options.evalStack.has(expressionId)) {
    return {
      ok: false,
      value: null,
      issues: [
        issue(
          'expression.cycle',
          `Circular expression reference detected: "${expressionId}".`,
        ),
      ],
    };
  }

  if (!ctx.expressionEngine) {
    return {
      ok: false,
      value: null,
      issues: [
        issue(
          'semantic.invalid_expression_reference',
          'Expression engine is required to evaluate nested expression references.',
        ),
      ],
    };
  }

  const nextStack = new Set(options.evalStack);
  nextStack.add(expressionId);
  return ctx.expressionEngine.evaluate(expressionId, ctx, {
    profile: options.profile,
    expectedType: options.expectedType,
    evalStack: nextStack,
  });
}

function evalIf(node: ExpressionIfNode, ctx: BindingContext, options: Required<ExpressionEvaluationOptions>): ExpressionEvaluationResult {
  const condition = evalNode(node.when, ctx, options);
  if (!condition.ok) {
    return condition;
  }

  const boolCondition = toBoolean(condition.value);
  if (boolCondition === null) {
    return { ok: false, value: null, issues: [issue('expression.type_mismatch', 'If condition must evaluate to boolean.')] };
  }

  return boolCondition ? evalNode(node.then, ctx, options) : evalNode(node.else, ctx, options);
}

function evalNode(node: ExpressionAstNode, ctx: BindingContext, options: Required<ExpressionEvaluationOptions>): ExpressionEvaluationResult {
  switch (node.kind) {
    case 'literal':
      return { ok: true, value: node.value, issues: [] };

    case 'ref': {
      if (node.ref.kind === 'expression') {
        return evalExpressionLibraryRef(node.ref.expressionId, ctx, options);
      }

      const resolved = resolveRef(node.ref, ctx);
      if (resolved.found) {
        const value = resolved.value;
        if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          return { ok: true, value, issues: [] };
        }
        return {
          ok: false,
          value: null,
          issues: [issue('expression.type_mismatch', 'Resolved reference is not a supported scalar value.')],
        };
      }

      if (options.expectedType === 'boolean' && options.profile !== 'production') {
        // DEC-019 fallback behavior for preview/dev profile: fail-open for unresolved
        // expression-driven visibility/enabled decisions.
        return {
          ok: true,
          value: true,
          issues: [
            {
              severity: 'warning',
              code: 'render.visibility_expression_unavailable',
              message: 'Expression reference could not be resolved; using fail-open fallback.',
            },
          ],
        };
      }

      return {
        ok: false,
        value: null,
        issues: [issue('semantic.invalid_data_ref', 'Expression reference could not be resolved.')],
      };
    }

    case 'unary':
      return evalUnary(node, ctx, options);

    case 'binary':
      return evalBinary(node, ctx, options);

    case 'if':
      return evalIf(node, ctx, options);

    case 'callPure':
      return evalCallPure(node, ctx, options);
  }
}

export function evaluateExpressionRoot(
  root: Record<string, unknown>,
  context: BindingContext,
  options?: ExpressionEvaluationOptions,
): ExpressionEvaluationResult {
  const mergedOptions: Required<ExpressionEvaluationOptions> = {
    profile: options?.profile ?? DEFAULT_PROFILE,
    expectedType: options?.expectedType ?? 'boolean',
    evalStack: options?.evalStack ?? new Set<string>(),
  };

  const ast = asNode(root);
  if (!ast) {
    return {
      ok: false,
      value: null,
      issues: [issue('expression.invalid_node', 'Expression root is not a valid expression AST node.')],
    };
  }

  return evalNode(ast, context, mergedOptions);
}

export function evaluateExpressionDefinition(
  expression: ExpressionDefinition,
  context: BindingContext,
  options?: ExpressionEvaluationOptions,
): ExpressionEvaluationResult {
  return evaluateExpressionRoot(expression.root, context, {
    ...options,
    expectedType: options?.expectedType ?? (expression.outputType as ExpressionEvaluationOptions['expectedType'] | undefined),
  });
}

/**
 * Looks up an expression by ID in the provided library and evaluates it.
 *
 * Returns a diagnostic result with code 'semantic.invalid_expression_reference'
 * when the ID is not found in the library.
 */
export function evaluateExpressionById(
  expressionId: string,
  library: ExpressionDefinition[],
  context: BindingContext,
  options?: ExpressionEvaluationOptions,
): ExpressionEvaluationResult {
  const definition = library.find((e) => e.id === expressionId);
  if (!definition) {
    return {
      ok: false,
      value: null,
      issues: [
        issue(
          'semantic.invalid_expression_reference',
          `Expression "${expressionId}" not found in template library.`,
        ),
      ],
    };
  }
  return evaluateExpressionDefinition(definition, context, options);
}

/**
 * Creates an ExpressionEngine bound to the given expression library.
 *
 * The returned engine resolves expression IDs against the library at
 * evaluation time, enabling renderers and the preview pipeline to evaluate
 * named expressions without knowledge of the library structure.
 */
export function createExpressionEngine(library: ExpressionDefinition[]): ExpressionEngine {
  return {
    evaluate: (expressionId, context, options) =>
      evaluateExpressionById(expressionId, library, context, options),
  };
}
